import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  ServerIcon,
  CircleStackIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon,
  TableCellsIcon,
  AdjustmentsHorizontalIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  ShieldCheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const DB_CONFIGS = {
  postgresql: {
    name: 'PostgreSQL',
    defaultPort: 5432,
    icon: ServerIcon,
    color: 'text-indigo-500'
  },
  mysql: {
    name: 'MySQL',
    defaultPort: 3306,
    icon: ServerIcon,
    color: 'text-orange-500'
  },
  mongodb: {
    name: 'MongoDB',
    defaultPort: 27017,
    icon: CircleStackIcon,
    color: 'text-emerald-500',
    isNoSQL: true
  }
};

export default function DatabaseSource({ type, companyId, onSuccess, onBack }) {
  const dbConfig = DB_CONFIGS[type];
  const isNoSQL = dbConfig.isNoSQL;
  
  // Connection settings
  const [connectionType, setConnectionType] = useState('standard'); // standard or uri
  const [host, setHost] = useState('');
  const [port, setPort] = useState(dbConfig.defaultPort);
  const [database, setDatabase] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [connectionUri, setConnectionUri] = useState('');
  const [ssl, setSsl] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Region whitelist IPs
  const [regionWhitelistIps, setRegionWhitelistIps] = useState([]);
  
  // Data source settings
  const [name, setName] = useState('');
  const [selectedTable, setSelectedTable] = useState('');
  const [tables, setTables] = useState([]);
  const [columns, setColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState(new Set());
  const [columnSearchTerm, setColumnSearchTerm] = useState('');
  
  // MongoDB specific
  const [flattenConfig, setFlattenConfig] = useState({
    enabled: true,
    maxDepth: 3,
    arrayHandling: 'first', // first, stringify, join
    separator: '.'
  });
  const [mongoSchema, setMongoSchema] = useState(null);
  const [flattenedPreview, setFlattenedPreview] = useState(null);
  const [importFilter, setImportFilter] = useState('');
  const [filterError, setFilterError] = useState(null);
  
  // SQL specific (PostgreSQL/MySQL)
  const [sqlQuery, setSqlQuery] = useState('');
  const [sqlQueryError, setSqlQueryError] = useState(null);
  const [useCustomQuery, setUseCustomQuery] = useState(false);
  const isSQL = type === 'postgresql' || type === 'mysql';
  
  // Incremental sync config (for all database types)
  const [incrementalEnabled, setIncrementalEnabled] = useState(false);
  const [incrementalColumn, setIncrementalColumn] = useState('');
  const [incrementalColumnType, setIncrementalColumnType] = useState('auto');
  
  // Column type options for incremental sync
  const getColumnTypeOptions = () => [
    { value: 'auto', label: 'Auto-detect', description: 'Automatically detect date formats' },
    { value: 'date', label: 'Date', description: 'Date values (e.g., 2026-01-21)' },
    { value: 'datetime', label: 'Date/Time', description: 'Timestamp values (e.g., 2026-01-21T10:30:00Z)' },
    { value: 'number', label: 'Number', description: 'Numeric values (e.g., auto-incrementing IDs)' },
    { value: 'string', label: 'String', description: 'Text values (compared alphabetically)' },
    ...(isNoSQL ? [{ value: 'objectid', label: 'ObjectId', description: 'MongoDB ObjectId (_id field)' }] : [])
  ];
  
  // Deduplication config
  const [deduplicationEnabled, setDeduplicationEnabled] = useState(false);
  const [deduplicateKeyColumns, setDeduplicateKeyColumns] = useState([]);
  
  // State
  const [step, setStep] = useState(1); // 1: connection, 2: table selection, 3: columns/preview
  const [isConnecting, setIsConnecting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  
  // Track if this is the initial load to avoid auto-refresh on mount
  const isInitialMount = useRef(true);
  const debounceTimer = useRef(null);
  
  // Fetch region whitelist IPs on mount
  useEffect(() => {
    const fetchRegionInfo = async () => {
      try {
        const response = await api.get('/companies/my-endpoint', {
          params: companyId ? { companyId } : {}
        });
        if (response.data.whitelistIps && Array.isArray(response.data.whitelistIps)) {
          setRegionWhitelistIps(response.data.whitelistIps);
        }
      } catch (error) {
        console.error('Failed to fetch region info:', error);
        // Not critical, just means we can't show the whitelist IPs
      }
    };
    fetchRegionInfo();
  }, [companyId]);
  
  // Auto-refresh preview when flattening options change (for MongoDB)
  useEffect(() => {
    // Skip on initial mount and when not on step 3
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Only auto-refresh for MongoDB when on step 3 with a selected table
    if (!isNoSQL || step !== 3 || !selectedTable) return;
    
    // Debounce the refresh to avoid too many API calls
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    debounceTimer.current = setTimeout(() => {
      console.log('[MongoDB] Auto-refreshing preview with new flattenConfig:', flattenConfig);
      handleFlattenPreview();
    }, 500);
    
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [flattenConfig.maxDepth, flattenConfig.arrayHandling, flattenConfig.separator]);
  
  const buildConnectionConfig = () => {
    if (connectionType === 'uri') {
      return { uri: connectionUri };
    }
    return {
      host,
      port: parseInt(port),
      database,
      username,
      password,
      ssl
    };
  };
  
  // Validate MongoDB filter JSON
  const validateFilter = (filterStr) => {
    if (!filterStr || filterStr.trim() === '') {
      setFilterError(null);
      return true;
    }
    try {
      const parsed = JSON.parse(filterStr);
      if (typeof parsed !== 'object' || Array.isArray(parsed)) {
        setFilterError('Filter must be a JSON object');
        return false;
      }
      setFilterError(null);
      return true;
    } catch (e) {
      setFilterError('Invalid JSON format');
      return false;
    }
  };
  
  const handleFilterChange = (value) => {
    setImportFilter(value);
    validateFilter(value);
  };
  
  // Validate SQL query (basic validation)
  const validateSqlQuery = (query) => {
    if (!query || query.trim() === '') {
      setSqlQueryError(null);
      return true;
    }
    const trimmed = query.trim().toLowerCase();
    // Must start with SELECT
    if (!trimmed.startsWith('select')) {
      setSqlQueryError('Query must start with SELECT');
      return false;
    }
    // Basic SQL injection protection - no semicolons (prevents multiple statements)
    if (query.includes(';') && query.trim().indexOf(';') < query.trim().length - 1) {
      setSqlQueryError('Multiple statements are not allowed');
      return false;
    }
    // No dangerous keywords
    const dangerousKeywords = ['drop ', 'delete ', 'update ', 'insert ', 'truncate ', 'alter ', 'create ', 'grant ', 'revoke '];
    for (const keyword of dangerousKeywords) {
      if (trimmed.includes(keyword)) {
        setSqlQueryError(`Queries containing "${keyword.trim().toUpperCase()}" are not allowed`);
        return false;
      }
    }
    setSqlQueryError(null);
    return true;
  };
  
  const handleSqlQueryChange = (value) => {
    setSqlQuery(value);
    validateSqlQuery(value);
  };
  
  const handleTestConnection = async () => {
    setIsConnecting(true);
    setConnectionStatus(null);
    
    try {
      const result = await api.post('/data-sources/test-database', {
        type,
        config: buildConnectionConfig()
      });
      
      setConnectionStatus({
        success: result.data.success,
        message: result.data.message
      });
      
      if (result.data.success) {
        toast.success('Connection successful!');
        setTables(result.data.tables || []);
        setStep(2);
      } else {
        toast.error(result.data.message || 'Connection failed');
      }
    } catch (error) {
      console.error('Connection test error:', error);
      setConnectionStatus({
        success: false,
        message: error.response?.data?.error || 'Connection failed'
      });
      toast.error('Failed to connect');
    } finally {
      setIsConnecting(false);
    }
  };
  
  const handleTableSelect = async (tableName) => {
    setSelectedTable(tableName);
    setIsLoading(true);
    
    try {
      const requestBody = {
        type,
        config: buildConnectionConfig(),
        table: tableName,
        limit: 20
      };
      
      // Include import filter and flatten config for MongoDB
      if (isNoSQL) {
        if (importFilter && importFilter.trim()) {
          requestBody.importFilter = importFilter;
        }
        requestBody.flattenConfig = flattenConfig;
      }
      
      // Include SQL query for PostgreSQL/MySQL if using custom query
      if (isSQL && useCustomQuery && sqlQuery.trim()) {
        requestBody.sqlQuery = sqlQuery;
      }
      
      const result = await api.post('/data-sources/preview-table', requestBody);
      
      if (isNoSQL) {
        // MongoDB - need to show flattening options
        setMongoSchema(result.data.schema);
        setFlattenedPreview(result.data.flattenedData);
        setPreviewData(result.data.rawData || []);
        // Initialize selected columns from flattened schema
        const schemaColumns = result.data.schema?.columns || [];
        setColumns(schemaColumns);
        setSelectedColumns(new Set(schemaColumns.map(c => c.name)));
      } else {
        // SQL databases
        setColumns(result.data.columns || []);
        setSelectedColumns(new Set(result.data.columns?.map(c => c.name) || []));
        setPreviewData(result.data.preview || []);
      }
      
      setStep(3);
    } catch (error) {
      console.error('Preview error:', error);
      // Show detailed error message if available
      const errorDetails = error.response?.data?.details || error.response?.data?.error || 'Failed to load table preview';
      toast.error(errorDetails, { duration: 6000 });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleColumnToggle = (columnName) => {
    const updated = new Set(selectedColumns);
    if (updated.has(columnName)) {
      updated.delete(columnName);
    } else {
      updated.add(columnName);
    }
    setSelectedColumns(updated);
  };
  
  const handleSelectAllColumns = () => {
    if (selectedColumns.size === columns.length) {
      setSelectedColumns(new Set());
    } else {
      setSelectedColumns(new Set(columns.map(c => c.name)));
    }
  };
  
  const handleFlattenPreview = async () => {
    // Validate filter before making request
    if (importFilter && !validateFilter(importFilter)) {
      toast.error('Please fix the filter JSON before updating preview');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const requestBody = {
        type,
        config: buildConnectionConfig(),
        collection: selectedTable,
        flattenConfig,
        limit: 20
      };
      
      // Include import filter if set
      if (importFilter && importFilter.trim()) {
        requestBody.importFilter = importFilter;
      }
      
      const result = await api.post('/data-sources/flatten-preview', requestBody);
      
      setFlattenedPreview(result.data.flattenedData);
      setMongoSchema(result.data.schema);
      
      // Update columns from new schema, preserving existing selections where possible
      const newColumns = result.data.schema?.columns || [];
      setColumns(newColumns);
      // Keep previously selected columns that still exist, select all new ones
      const newColumnNames = new Set(newColumns.map(c => c.name));
      const updatedSelection = new Set(
        [...selectedColumns].filter(name => newColumnNames.has(name))
      );
      // If no columns were previously selected (first load), select all
      if (updatedSelection.size === 0) {
        setSelectedColumns(new Set(newColumns.map(c => c.name)));
      } else {
        setSelectedColumns(updatedSelection);
      }
    } catch (error) {
      console.error('Flatten preview error:', error);
      toast.error('Failed to generate preview');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    
    if (!selectedTable) {
      toast.error('Please select a table');
      return;
    }
    
    if (selectedColumns.size === 0) {
      toast.error(`Please select at least one ${isNoSQL ? 'field' : 'column'}`);
      return;
    }
    
    setIsCreating(true);
    
    try {
      // Get full column schema for selected columns (with types)
      const selectedColumnSchema = columns.filter(c => selectedColumns.has(c.name));
      
      // Parse import filter for MongoDB
      let parsedFilter = null;
      if (isNoSQL && importFilter && importFilter.trim()) {
        try {
          parsedFilter = JSON.parse(importFilter);
        } catch (e) {
          toast.error('Invalid import filter JSON');
          setIsCreating(false);
          return;
        }
      }
      
      const connectionConfig = {
        ...buildConnectionConfig(),
        table: selectedTable,
        columns: Array.from(selectedColumns),
        flattenConfig: isNoSQL ? flattenConfig : undefined,
        importFilter: isNoSQL ? parsedFilter : undefined,
        sqlQuery: (isSQL && useCustomQuery && sqlQuery.trim()) ? sqlQuery : undefined,
        incrementalConfig: incrementalEnabled && incrementalColumn ? {
          enabled: true,
          column: incrementalColumn,
          columnType: incrementalColumnType || 'auto'
        } : { enabled: false },
        deduplication: deduplicationEnabled && deduplicateKeyColumns.length > 0 ? {
          enabled: true,
          keyColumns: deduplicateKeyColumns
        } : { enabled: false, keyColumns: [] }
      };
      
      const result = await api.post('/data-sources', {
        name,
        type,
        companyId,
        connectionConfig,
        // Send full schema info for database sources
        schemaInfo: {
          tables: [{
            name: selectedTable,
            columns: selectedColumnSchema
          }]
        }
      });
      
      onSuccess(result.data.dataSource);
    } catch (error) {
      console.error('Create error:', error);
      toast.error(error.response?.data?.error || 'Failed to create data source');
    } finally {
      setIsCreating(false);
    }
  };
  
  const Icon = dbConfig.icon;
  
  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center gap-4">
        {[
          { num: 1, label: 'Connect' },
          { num: 2, label: isNoSQL ? 'Collection' : 'Table' },
          { num: 3, label: 'Configure' }
        ].map((s, i) => (
          <div key={s.num} className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm ${
              step >= s.num 
                ? 'bg-primary-600 text-white' 
                : 'bg-surface-200 dark:bg-surface-700 text-surface-500'
            }`}>
              {step > s.num ? <CheckCircleIcon className="w-5 h-5" /> : s.num}
            </div>
            <span className={`text-sm ${step >= s.num ? 'text-surface-900 dark:text-white' : 'text-surface-500'}`}>
              {s.label}
            </span>
            {i < 2 && <div className="w-12 h-px bg-surface-200 dark:bg-surface-700" />}
          </div>
        ))}
      </div>
      
      {/* Step 1: Connection */}
      {step === 1 && (
        <div className="card p-6 space-y-6">
          <div className="flex items-center gap-3 mb-4">
            <Icon className={`w-8 h-8 ${dbConfig.color}`} />
            <h3 className="text-lg font-semibold text-surface-900 dark:text-white">
              Connect to {dbConfig.name}
            </h3>
          </div>
          
          {/* Whitelist IP Information */}
          {regionWhitelistIps.length > 0 && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-start gap-3">
                <ShieldCheckIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
                    Firewall Configuration Required
                  </h4>
                  <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                    If your database is behind a firewall, please whitelist the following IP address{regionWhitelistIps.length > 1 ? 'es' : ''} to allow our service to connect:
                  </p>
                  <div className="bg-white dark:bg-surface-800 rounded border border-blue-200 dark:border-blue-700 p-3">
                    {regionWhitelistIps.map((ip, index) => (
                      <div key={index} className="flex items-center gap-2 mb-1 last:mb-0">
                        <code className="text-sm font-mono text-blue-900 dark:text-blue-100 font-semibold">
                          {ip}
                        </code>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(ip);
                            toast.success('IP address copied to clipboard!');
                          }}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          Copy
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
                    Add {regionWhitelistIps.length > 1 ? 'these IP addresses' : 'this IP address'} to your database firewall rules or security group before testing the connection.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Connection type toggle */}
          <div className="flex gap-4 p-1 bg-surface-100 dark:bg-surface-800 rounded-lg w-fit">
            <button
              onClick={() => setConnectionType('standard')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                connectionType === 'standard'
                  ? 'bg-white dark:bg-surface-700 shadow text-surface-900 dark:text-white'
                  : 'text-surface-600 dark:text-surface-400'
              }`}
            >
              Standard
            </button>
            <button
              onClick={() => setConnectionType('uri')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                connectionType === 'uri'
                  ? 'bg-white dark:bg-surface-700 shadow text-surface-900 dark:text-white'
                  : 'text-surface-600 dark:text-surface-400'
              }`}
            >
              Connection URI
            </button>
          </div>
          
          {connectionType === 'uri' ? (
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                Connection URI
              </label>
              <input
                type="text"
                value={connectionUri}
                onChange={(e) => setConnectionUri(e.target.value)}
                className="input font-mono text-sm"
                placeholder={
                  type === 'mongodb'
                    ? 'mongodb+srv://user:password@cluster.mongodb.net/database'
                    : `${type}://user:password@host:port/database`
                }
              />
              {type === 'mongodb' && (
                <p className="text-sm text-surface-500 mt-2">
                  Supports MongoDB Atlas connection strings
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  Host
                </label>
                <input
                  type="text"
                  value={host}
                  onChange={(e) => setHost(e.target.value)}
                  className="input"
                  placeholder="localhost or your-cluster.mongodb.net"
                />
              </div>
              
              <div className="col-span-2 sm:col-span-1">
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  Port
                </label>
                <input
                  type="number"
                  value={port}
                  onChange={(e) => setPort(e.target.value)}
                  className="input"
                  placeholder={dbConfig.defaultPort}
                />
              </div>
              
              <div className="col-span-2">
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  Database Name
                </label>
                <input
                  type="text"
                  value={database}
                  onChange={(e) => setDatabase(e.target.value)}
                  className="input"
                  placeholder="my_database"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="input"
                  placeholder="Username"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input pr-10"
                    placeholder="Password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400"
                  >
                    {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <div className="col-span-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ssl}
                    onChange={(e) => setSsl(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm text-surface-700 dark:text-surface-300">
                    Use SSL/TLS connection
                  </span>
                </label>
              </div>
            </div>
          )}
          
          {/* Connection status */}
          {connectionStatus && (
            <div className={`flex items-center gap-2 p-3 rounded-lg ${
              connectionStatus.success
                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
            }`}>
              {connectionStatus.success ? (
                <CheckCircleIcon className="w-5 h-5" />
              ) : (
                <ExclamationCircleIcon className="w-5 h-5" />
              )}
              <span className="text-sm">{connectionStatus.message}</span>
            </div>
          )}
          
          <div className="flex justify-between pt-4">
            <button onClick={onBack} className="btn-secondary">
              Back
            </button>
            <button
              onClick={handleTestConnection}
              disabled={isConnecting}
              className="btn-primary"
            >
              {isConnecting ? (
                <>
                  <ArrowPathIcon className="w-5 h-5 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <ServerIcon className="w-5 h-5" />
                  Test Connection
                </>
              )}
            </button>
          </div>
        </div>
      )}
      
      {/* Step 2: Table/Collection Selection */}
      {step === 2 && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-surface-900 dark:text-white">
              Select {isNoSQL ? 'Collection' : 'Table'}
            </h3>
            <span className="text-sm text-surface-500">
              {tables.length} {isNoSQL ? 'collections' : 'tables'} found
            </span>
          </div>
          
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <ArrowPathIcon className="w-8 h-8 text-primary-500 animate-spin" />
            </div>
          ) : tables.length === 0 ? (
            <div className="text-center py-8 text-surface-500">
              No {isNoSQL ? 'collections' : 'tables'} found in database
            </div>
          ) : (
            <div className="grid gap-2 max-h-96 overflow-y-auto">
              {tables.map((table) => (
                <button
                  key={table.name || table}
                  onClick={() => handleTableSelect(table.name || table)}
                  className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                    selectedTable === (table.name || table)
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-surface-200 dark:border-surface-700 hover:border-primary-300'
                  }`}
                >
                  <TableCellsIcon className="w-5 h-5 text-surface-400" />
                  <div className="flex-1">
                    <p className="font-medium text-surface-900 dark:text-white">
                      {table.name || table}
                    </p>
                    {table.rowCount !== undefined && (
                      <p className="text-sm text-surface-500">
                        {table.rowCount.toLocaleString()} rows
                      </p>
                    )}
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-surface-400" />
                </button>
              ))}
            </div>
          )}
          
          <div className="flex justify-between pt-4 mt-4 border-t border-surface-200 dark:border-surface-700">
            <button onClick={() => setStep(1)} className="btn-secondary">
              Back
            </button>
          </div>
        </div>
      )}
      
      {/* Step 3: Configure & Preview */}
      {step === 3 && (
        <div className="space-y-6">
          {/* Name input */}
          <div className="card p-4">
            <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
              Data Source Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder={`${selectedTable} data`}
            />
          </div>
          
          {/* MongoDB Import Filter */}
          {isNoSQL && (
            <div className="card p-4">
              <div className="flex items-center gap-2 mb-4">
                <svg className="w-5 h-5 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <h4 className="font-medium text-surface-900 dark:text-white">
                  Import Filter
                </h4>
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                  Optional
                </span>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                  MongoDB Query Filter (JSON)
                </label>
                <textarea
                  value={importFilter}
                  onChange={(e) => handleFilterChange(e.target.value)}
                  className={`input font-mono text-sm h-24 ${filterError ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder='{"status": "active", "projectId": {"$oid": "6895bb8dbcff4243c21386f7"}}'
                />
                {filterError && (
                  <p className="text-sm text-red-500 mt-1">{filterError}</p>
                )}
                <div className="mt-2 space-y-2">
                  <p className="text-sm text-surface-500">
                    Filter documents during import using MongoDB Extended JSON syntax.
                  </p>
                  <div className="text-xs text-surface-400 space-y-1">
                    <p>Examples:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-2">
                      <li><code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">{`{"active": true}`}</code> - Simple field match</li>
                      <li><code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">{`{"projectId": {"$oid": "..."}}`}</code> - ObjectId filter</li>
                      <li><code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">{`{"createdAt": {"$gte": {"$date": "2024-01-01"}}}`}</code> - Date filter</li>
                      <li><code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">{`{"status": {"$in": ["active", "pending"]}}`}</code> - Multiple values</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* SQL Query Filter (PostgreSQL/MySQL) */}
          {isSQL && (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <h4 className="font-medium text-surface-900 dark:text-white">
                    Custom SQL Query
                  </h4>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                    Optional
                  </span>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useCustomQuery}
                    onChange={(e) => setUseCustomQuery(e.target.checked)}
                    className="rounded text-primary-600"
                  />
                  <span className="text-sm text-surface-700 dark:text-surface-300">
                    Use custom query
                  </span>
                </label>
              </div>
              
              {useCustomQuery && (
                <div>
                  <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                    SQL Query
                  </label>
                  <textarea
                    value={sqlQuery}
                    onChange={(e) => handleSqlQueryChange(e.target.value)}
                    className={`input font-mono text-sm h-32 ${sqlQueryError ? 'border-red-500 focus:ring-red-500' : ''}`}
                    placeholder={`SELECT *\nFROM ${selectedTable}\nWHERE status = 'active'\nLIMIT 100000`}
                  />
                  {sqlQueryError && (
                    <p className="text-sm text-red-500 mt-1">{sqlQueryError}</p>
                  )}
                  <div className="mt-2 space-y-2">
                    <p className="text-sm text-surface-500">
                      Write a custom SELECT query to filter or transform your data during import.
                    </p>
                    <div className="text-xs text-surface-400 space-y-1">
                      <p>Examples:</p>
                      <ul className="list-disc list-inside space-y-0.5 ml-2">
                        <li><code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">SELECT * FROM {selectedTable} WHERE created_at {'>'} '2024-01-01'</code></li>
                        <li><code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">SELECT id, name, email FROM {selectedTable} WHERE active = true</code></li>
                        <li><code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">SELECT t.*, c.name as category FROM {selectedTable} t JOIN categories c ON t.cat_id = c.id</code></li>
                      </ul>
                    </div>
                    <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-xs text-amber-700 dark:text-amber-400">
                      <strong>Note:</strong> Only SELECT queries are allowed. A LIMIT clause is recommended for large tables.
                    </div>
                  </div>
                  
                  {sqlQuery.trim() && !sqlQueryError && (
                    <button
                      onClick={() => handleTableSelect(selectedTable)}
                      disabled={isLoading}
                      className="mt-3 btn-secondary text-sm"
                    >
                      {isLoading ? (
                        <>
                          <ArrowPathIcon className="w-4 h-4 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        'Preview Query Results'
                      )}
                    </button>
                  )}
                </div>
              )}
              
              {!useCustomQuery && (
                <p className="text-sm text-surface-500">
                  Enable custom query to filter data with SQL WHERE clauses, JOINs, or computed columns.
                </p>
              )}
            </div>
          )}

          {/* Incremental Sync Configuration */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ClockIcon className="w-5 h-5 text-surface-400" />
                <h4 className="font-medium text-surface-900 dark:text-white">
                  Incremental Sync
                </h4>
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                  Optional
                </span>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={incrementalEnabled}
                  onChange={(e) => setIncrementalEnabled(e.target.checked)}
                  className="rounded text-primary-600"
                />
                <span className="text-sm text-surface-700 dark:text-surface-300">
                  Enable
                </span>
              </label>
            </div>
            
            {!incrementalEnabled ? (
              <p className="text-sm text-surface-500">
                Enable incremental sync to only fetch new or updated records on subsequent syncs, 
                instead of re-fetching all data every time.
              </p>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-surface-500 mb-4">
                  Configure which column to track for identifying new or updated records.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                      Tracking Column
                    </label>
                    {columns.length > 0 ? (
                      <select
                        value={incrementalColumn}
                        onChange={(e) => setIncrementalColumn(e.target.value)}
                        className="input"
                      >
                        <option value="">Select a column...</option>
                        {columns.map(col => (
                          <option key={col.name} value={col.name}>
                            {col.name} ({col.type})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={incrementalColumn}
                        onChange={(e) => setIncrementalColumn(e.target.value)}
                        className="input font-mono text-sm"
                        placeholder="updated_at"
                      />
                    )}
                    <p className="text-xs text-surface-500 mt-1">
                      {isNoSQL 
                        ? 'The field to track for changes (e.g., updatedAt, _id, createdAt)'
                        : 'The column to track for changes (e.g., updated_at, id, created_at)'
                      }
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                      Column Data Type
                    </label>
                    <select
                      value={incrementalColumnType}
                      onChange={(e) => setIncrementalColumnType(e.target.value)}
                      className="input"
                    >
                      {getColumnTypeOptions().map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-surface-500 mt-1">
                      {getColumnTypeOptions().find(o => o.value === incrementalColumnType)?.description || 'Select the data type of the tracking column'}
                    </p>
                  </div>
                </div>
                
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-400">
                    <strong>How it works:</strong> After the first full sync, subsequent syncs will only fetch records where 
                    <code className="mx-1 px-1 bg-blue-100 dark:bg-blue-800 rounded">{incrementalColumn || 'column'}</code> 
                    is greater than the last synced value.
                    {isNoSQL 
                      ? ' For MongoDB, this adds a filter like {column: {$gt: lastValue}}.'
                      : ' For SQL databases, this adds a WHERE clause like "column > lastValue".'
                    }
                  </p>
                </div>
                
                <div className="text-xs text-surface-400 space-y-1">
                  <p><strong>Recommended columns:</strong></p>
                  <ul className="list-disc list-inside space-y-0.5 ml-2">
                    <li><code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">{isNoSQL ? 'updatedAt' : 'updated_at'}</code> - Best for tracking modified records</li>
                    <li><code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">{isNoSQL ? 'createdAt' : 'created_at'}</code> - For append-only data</li>
                    <li><code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">{isNoSQL ? '_id' : 'id'}</code> - For auto-incrementing IDs</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Deduplication Configuration */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <TableCellsIcon className="w-5 h-5 text-surface-400" />
                <h4 className="font-medium text-surface-900 dark:text-white">
                  Deduplication
                </h4>
                <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
                  Optional
                </span>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deduplicationEnabled}
                  onChange={(e) => setDeduplicationEnabled(e.target.checked)}
                  className="rounded text-primary-600"
                />
                <span className="text-sm text-surface-700 dark:text-surface-300">
                  Enable
                </span>
              </label>
            </div>
            
            {!deduplicationEnabled ? (
              <p className="text-sm text-surface-500">
                Enable deduplication to automatically remove duplicate rows during imports based on unique key columns.
              </p>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-surface-500 mb-4">
                  Select the column(s) that uniquely identify each record. Rows with the same values in these columns will be considered duplicates.
                </p>
                
                <div>
                  <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                    Unique Key Column(s)
                  </label>
                  
                  {columns.length > 0 ? (
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-2 p-3 border border-surface-200 dark:border-surface-700 rounded-lg min-h-[42px]">
                        {deduplicateKeyColumns.length === 0 ? (
                          <span className="text-surface-400 text-sm">Click columns below to select unique keys...</span>
                        ) : (
                          deduplicateKeyColumns.map(col => (
                            <span 
                              key={col}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-md text-sm"
                            >
                              {col}
                              <button
                                type="button"
                                onClick={() => setDeduplicateKeyColumns(prev => prev.filter(c => c !== col))}
                                className="hover:text-primary-900 dark:hover:text-primary-100"
                              >
                                <XMarkIcon className="w-4 h-4" />
                              </button>
                            </span>
                          ))
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                        {columns.map(col => {
                          const isSelected = deduplicateKeyColumns.includes(col.name);
                          return (
                            <button
                              key={col.name}
                              type="button"
                              onClick={() => {
                                setDeduplicateKeyColumns(prev => 
                                  isSelected 
                                    ? prev.filter(c => c !== col.name)
                                    : [...prev, col.name]
                                );
                              }}
                              className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                                isSelected
                                  ? 'bg-primary-500 text-white border-primary-500'
                                  : 'bg-surface-100 dark:bg-surface-800 text-surface-700 dark:text-surface-300 border-surface-300 dark:border-surface-600 hover:border-primary-400'
                              }`}
                            >
                              {col.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={deduplicateKeyColumns.join(', ')}
                      onChange={(e) => setDeduplicateKeyColumns(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                      className="input font-mono text-sm"
                      placeholder={isNoSQL ? "_id or email" : "id or email"}
                    />
                  )}
                  
                  <p className="text-xs text-surface-500 mt-1">
                    {isNoSQL 
                      ? 'The field(s) to use as unique keys (e.g., _id, email, userId)'
                      : 'The column(s) to use as unique keys (e.g., id, email, user_id)'
                    }
                  </p>
                </div>
                
                <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <p className="text-sm text-purple-700 dark:text-purple-400">
                    <strong>How it works:</strong> During each import, if duplicate rows are found based on 
                    {deduplicateKeyColumns.length > 0 ? (
                      <code className="mx-1 px-1 bg-purple-100 dark:bg-purple-800 rounded">
                        {deduplicateKeyColumns.join(', ')}
                      </code>
                    ) : (
                      ' the selected columns'
                    )}, 
                    only one row will be kept. This helps maintain data quality by preventing duplicate records in your data warehouse.
                  </p>
                </div>
                
                <div className="text-xs text-surface-400 space-y-1">
                  <p><strong>Recommended key columns:</strong></p>
                  <ul className="list-disc list-inside space-y-0.5 ml-2">
                    <li><code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">{isNoSQL ? '_id' : 'id'}</code> - Primary key or unique identifier</li>
                    <li><code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">email</code> - For user records</li>
                    <li><code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">{isNoSQL ? 'orderId' : 'order_id'}</code> - For transactional data</li>
                    <li>Or use multiple columns like <code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">user_id + date</code> for composite keys</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* MongoDB Flattening Options */}
          {isNoSQL && (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <AdjustmentsHorizontalIcon className="w-5 h-5 text-surface-400" />
                  <h4 className="font-medium text-surface-900 dark:text-white">
                    Flattening Options
                  </h4>
                </div>
                <button
                  onClick={handleFlattenPreview}
                  disabled={isLoading || !!filterError}
                  className="btn-secondary text-sm"
                >
                  {isLoading ? (
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                  ) : (
                    'Update Preview'
                  )}
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                    Max Nesting Depth
                  </label>
                  <input
                    type="number"
                    value={flattenConfig.maxDepth}
                    onChange={(e) => setFlattenConfig({ ...flattenConfig, maxDepth: parseInt(e.target.value) })}
                    className="input"
                    min={1}
                    max={10}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                    Array Handling
                  </label>
                  <select
                    value={flattenConfig.arrayHandling}
                    onChange={(e) => setFlattenConfig({ ...flattenConfig, arrayHandling: e.target.value })}
                    className="input"
                  >
                    <option value="first">Take first element (flatten nested)</option>
                    <option value="stringify">Convert to JSON string</option>
                    <option value="join">Join values with comma</option>
                  </select>
                  <p className="text-xs text-surface-500 mt-1">
                    {flattenConfig.arrayHandling === 'first' && 'Takes the first array item and flattens its nested fields'}
                    {flattenConfig.arrayHandling === 'stringify' && 'Converts entire array to a JSON string column'}
                    {flattenConfig.arrayHandling === 'join' && 'Joins array values into a comma-separated string'}
                  </p>
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                    Key Separator
                  </label>
                  <input
                    type="text"
                    value={flattenConfig.separator}
                    onChange={(e) => setFlattenConfig({ ...flattenConfig, separator: e.target.value })}
                    className="input w-24"
                    placeholder="."
                  />
                  <p className="text-sm text-surface-500 mt-1">
                    Nested keys will be joined with this separator (e.g., address.city)
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Column Selection (for both SQL and MongoDB) */}
          {columns.length > 0 && (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-medium text-surface-900 dark:text-white">
                    Select {isNoSQL ? 'Fields' : 'Columns'} ({selectedColumns.size} of {columns.length})
                  </h4>
                  {isNoSQL && (
                    <p className="text-sm text-surface-500 mt-1">
                      Choose which flattened fields to include in your data source
                    </p>
                  )}
                </div>
                <button
                  onClick={handleSelectAllColumns}
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {selectedColumns.size === columns.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              
              {/* Column Search */}
              {columns.length > 8 && (
                <div className="relative mb-3">
                  <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
                  <input
                    type="text"
                    value={columnSearchTerm}
                    onChange={(e) => setColumnSearchTerm(e.target.value)}
                    placeholder={`Search ${isNoSQL ? 'fields' : 'columns'}...`}
                    className="input pl-9 py-2 text-sm w-full"
                  />
                  {columnSearchTerm && (
                    <button
                      onClick={() => setColumnSearchTerm('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600"
                    >
                      ×
                    </button>
                  )}
                </div>
              )}
              
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                {columns
                  .filter(col => col.name.toLowerCase().includes(columnSearchTerm.toLowerCase()))
                  .map((col) => (
                  <label
                    key={col.name}
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                      selectedColumns.has(col.name)
                        ? 'bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800'
                        : 'bg-surface-50 dark:bg-surface-800 border border-transparent hover:bg-surface-100 dark:hover:bg-surface-700'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedColumns.has(col.name)}
                      onChange={() => handleColumnToggle(col.name)}
                      className="rounded text-primary-600"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-surface-900 dark:text-white truncate" title={col.name}>
                        {col.name}
                      </p>
                      <p className="text-xs text-surface-500">{col.type}</p>
                    </div>
                  </label>
                ))}
                {columns.filter(col => col.name.toLowerCase().includes(columnSearchTerm.toLowerCase())).length === 0 && (
                  <div className="col-span-full text-center py-4 text-surface-500 text-sm">
                    No {isNoSQL ? 'fields' : 'columns'} match "{columnSearchTerm}"
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Data Preview */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-surface-900 dark:text-white">
                Data Preview {isNoSQL ? '(Flattened)' : ''}
              </h4>
              <span className="text-sm text-surface-500">
                Showing {selectedColumns.size} of {columns.length} {isNoSQL ? 'fields' : 'columns'}
              </span>
            </div>
            
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <ArrowPathIcon className="w-8 h-8 text-primary-500 animate-spin" />
              </div>
            ) : selectedColumns.size === 0 ? (
              <div className="text-center py-8 text-surface-500">
                Please select at least one {isNoSQL ? 'field' : 'column'} to preview
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-surface-200 dark:border-surface-700">
                <table className="w-full text-sm">
                  <thead className="bg-surface-50 dark:bg-surface-800">
                    <tr>
                      {columns.filter(c => selectedColumns.has(c.name)).map((col, i) => (
                        <th key={i} className="px-4 py-2 text-left font-medium text-surface-600 dark:text-surface-400 whitespace-nowrap">
                          {col.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(isNoSQL ? flattenedPreview : previewData)?.slice(0, 10).map((row, i) => (
                      <tr key={i} className="border-t border-surface-100 dark:border-surface-800">
                        {columns.filter(c => selectedColumns.has(c.name)).map((col, j) => (
                          <td key={j} className="px-4 py-2 text-surface-700 dark:text-surface-300 whitespace-nowrap max-w-xs truncate" title={String(row[col.name] ?? '')}>
                            {String(row[col.name] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex justify-between">
            <button onClick={() => setStep(2)} className="btn-secondary">
              Back
            </button>
            <button
              onClick={handleCreate}
              disabled={isCreating || !name.trim()}
              className="btn-primary"
            >
              {isCreating ? (
                <>
                  <ArrowPathIcon className="w-5 h-5 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-5 h-5" />
                  Create Data Source
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

